/**
 * The player activity log (player_events), read-only: MAD's six category chips (All, Auth,
 * Sessions, User, Finances, Economy, Game), a search over title, description, event type, IP
 * and event id, and paging, newest first.
 *
 * One component, two places: the player card's ACTIVITY LOG block (one player,
 * GET /admin/players/:id/activity) and the Activity log section (every player,
 * GET /admin/activity, plus a player search and a link from each row to the player card).
 */
import { el, clear } from '../../dom';
import {
  ACTIVITY_CATEGORIES, adminApi, type ActivityCategory, type AdminActivityEvent, type AdminActivityPage,
} from '../../game-api';
import { fmtDateTime, fmtNum } from './format';
import { apiMessage, btn, emptyBox, errorBox, input, pill, spinner, table } from './ui';

export const ACTIVITY_PAGE_SIZE = 20;
/** Same typing delay as the Players and Transactions searches. */
export const ACTIVITY_SEARCH_DEBOUNCE_MS = 300;

export const CATEGORY_LABEL: Record<ActivityCategory, string> = {
  auth: 'Auth', sessions: 'Sessions', user: 'User', finances: 'Finances', economy: 'Economy', game: 'Game',
};
const CATEGORY_TONE: Record<ActivityCategory, 'ok' | 'warn' | 'bad' | 'mute'> = {
  auth: 'bad', sessions: 'mute', user: 'warn', finances: 'ok', economy: 'ok', game: 'mute',
};

/** The tag a row shows; an unexpected value is shown as it came. */
export function categoryPill(category: string): HTMLElement {
  const known = (ACTIVITY_CATEGORIES as readonly string[]).includes(category) ? category as ActivityCategory : null;
  return pill(known ? CATEGORY_LABEL[known] : category || '—', known ? CATEGORY_TONE[known] : 'mute');
}

/** "1.2.3.4 · LV", "1.2.3.4", "LV" or "—". */
export function whereText(e: Pick<AdminActivityEvent, 'ipAddress' | 'geo'>): string {
  return [e.ipAddress, e.geo].filter((v): v is string => !!v).join(' · ') || '—';
}

export interface ActivityLogOptions {
  /** One player's log (the card); omitted = every player (the section). */
  playerId?: string;
  /** The card's player, named with their id above the log so it is clear whose log this is. */
  playerName?: string | null;
  /** Global feed: a row's player was clicked. */
  onOpenPlayer?: (e: AdminActivityEvent) => void;
}

export function activityLog(opts: ActivityLogOptions = {}): { node: HTMLElement; stop: () => void } {
  let cancelled = false;
  let seq = 0;
  const state = { page: 1, category: '' as ActivityCategory | '', search: '', player: '' };
  const global = !opts.playerId;

  const chips = el('div', 'flex flex-wrap gap-1.5');
  chips.setAttribute('role', 'group');
  chips.setAttribute('aria-label', 'Category');
  const chipButtons = new Map<string, HTMLButtonElement>();
  for (const [value, label] of [['', 'All'], ...ACTIVITY_CATEGORIES.map((c) => [c, CATEGORY_LABEL[c]])] as [ActivityCategory | '', string][]) {
    const b = btn(label, () => { state.category = value; state.page = 1; paintChips(); void load(); });
    chipButtons.set(value, b);
    chips.appendChild(b);
  }
  function paintChips() {
    for (const [value, b] of chipButtons) {
      const on = value === state.category;
      b.setAttribute('aria-pressed', String(on));
      b.classList.toggle('!text-[#ffe3a6]', on);
      b.classList.toggle('!border-[#c9a84c]', on);
    }
  }

  const search = input('Search title, description, type, IP or event id…');
  const player = global ? input('Player name or e-mail…') : null;
  const listHost = el('div', 'min-h-[80px]');
  const pager = el('div', 'flex items-center justify-between gap-2 mt-3 flex-wrap');

  // A new filter or search always starts again from page 1.
  let timer: ReturnType<typeof setTimeout> | undefined;
  const debounced = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      state.search = search.value.trim();
      state.player = player?.value.trim() ?? '';
      state.page = 1;
      void load();
    }, ACTIVITY_SEARCH_DEBOUNCE_MS);
  };
  search.addEventListener('input', debounced);
  player?.addEventListener('input', debounced);

  async function load() {
    const mine = ++seq;
    clear(listHost); clear(pager);
    listHost.appendChild(spinner());
    const q = { page: state.page, per_page: ACTIVITY_PAGE_SIZE, category: state.category, search: state.search };
    try {
      const page = opts.playerId
        ? await adminApi.playerActivity(opts.playerId, q)
        : await adminApi.activity({ ...q, player: state.player });
      if (cancelled || mine !== seq) return;
      renderPage(page);
    } catch (e) {
      if (cancelled || mine !== seq) return;
      clear(listHost);
      listHost.appendChild(errorBox(apiMessage(e, "Couldn't load the activity log."), () => void load()));
    }
  }

  function renderPage(page: AdminActivityPage) {
    const rows = page.events ?? [];
    clear(listHost); clear(pager);
    if (!rows.length) { listHost.appendChild(emptyBox('Nothing recorded for this filter.')); return; }

    const headers = ['When', 'Category', 'Event', 'IP / location'];
    if (global) headers.splice(1, 0, 'Player');
    listHost.appendChild(table(headers, rows.map((e) => {
      const cells: (Node | string)[] = [
        el('span', 'font-mono text-[11px] text-text-dim', {}, fmtDateTime(e.eventAt)),
        categoryPill(e.category),
        el('div', 'min-w-[14rem] max-w-[32rem] whitespace-normal break-words', {},
          el('div', 'text-sm', {}, e.title || e.eventType),
          e.description ? el('div', 'text-[11px] text-text-dim', {}, e.description) : null,
          el('div', 'text-[10px] text-text-dim/70 font-mono', { title: `Event id ${e.id}` }, e.eventType)),
        el('span', 'font-mono text-[11px] text-text-dim', {}, whereText(e)),
      ];
      if (global) {
        const label = e.user?.username ?? e.user?.email ?? e.userId;
        cells.splice(1, 0, el('div', 'flex flex-col', {},
          opts.onOpenPlayer
            ? el('button', 'text-left underline decoration-dotted text-[#ffd97a] hover:text-[#ffe3a6]',
              { type: 'button', title: 'Open the player card', onClick: () => opts.onOpenPlayer?.(e) }, label)
            : el('span', '', {}, label),
          el('span', 'font-mono text-[10px] text-text-dim select-all', { title: 'Player id' }, e.userId)));
      }
      return cells;
    })));

    const pages = Math.max(1, page.pages || 1);
    const current = page.page || state.page;
    pager.append(
      el('div', 'text-xs text-text-dim font-mono', {}, `${fmtNum(page.total ?? rows.length)} events`),
      el('div', 'flex gap-2 items-center', {},
        pageBtn('Prev', current > 1, () => { state.page = current - 1; void load(); }),
        el('span', 'text-xs text-text-dim font-mono', {}, `Page ${current} / ${pages}`),
        pageBtn('Next', current < pages, () => { state.page = current + 1; void load(); })));
  }

  paintChips();
  const node = el('div', 'flex flex-col gap-3', {},
    opts.playerId
      ? el('div', 'flex flex-wrap items-baseline gap-x-2 text-sm', {},
        el('span', 'text-[#ffd97a]', {}, opts.playerName || 'Player'),
        el('span', 'font-mono text-[11px] text-text-dim select-all', { title: 'Player id' }, `ID ${opts.playerId}`))
      : null,
    chips,
    el('div', 'flex flex-wrap gap-2', {},
      el('div', 'flex-1 min-w-[180px]', {}, search),
      player ? el('div', 'flex-1 min-w-[160px]', {}, player) : null),
    listHost, pager);
  void load();
  return { node, stop: () => { cancelled = true; clearTimeout(timer); } };
}

function pageBtn(label: string, enabled: boolean, onClick: () => void): HTMLButtonElement {
  const b = btn(label, () => { if (enabled) onClick(); });
  if (!enabled) { b.disabled = true; b.className += ' opacity-40 cursor-not-allowed'; }
  return b;
}
